// @flow
import React from 'react';
import {useParams} from "react-router-dom";
import FlexStack from "../components/Common/flex-stack";
import BaseData from "../components/AnimalDashboard/base-data-widget";
import PedigreeWidget from "../components/AnimalDashboard/Pedigree/pedigree-widget";
import {RegistryService} from "../services/registry-service";
import {RegistrationService} from "../services/registration-service";
import {RegistrationJson, RegistrationsController$RegistrationEditJson} from "../api/generated/registry-rest";
import RouteService from "../services/route-service";
import {AnimalBaseData, transformFromRegistration} from "../api/dtos/animal-base-data";
import {useGlobalSnackbarStore} from "../stores/global-snackbar-store";
import {OwnerWidget} from "../components/AnimalDashboard/owner-widget";
import {Widget} from "../components/Common";
import {useNav} from '../components/Common/hooks/use-nav';
import {
    Button,
    CircularProgress,
    LinearProgress,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Stack,
    Typography
} from "@mui/material";
import {useSuspenseAnimal} from "../components/Common/hooks/use-animal";
import {useMutation, useQuery, useQueryClient, useSuspenseQuery} from "@tanstack/react-query";
import IdentificationsWidget from "../components/AnimalDashboard/identifications-widget";
import {CertificateService} from "../services/certificate-service";
import {FileDownload, HistoryEdu, UploadFile} from "@mui/icons-material";
import {InfoBox} from "../components/Common/alerts";
import {JsonAnimal} from "../api/generated/rest-dto";
import {useBreederInfo} from "../hooks/use-breeders";
import {DnaService} from "../services/dna-service";
import AuthService from "../services/auth-service";
import {PANON_ADMIN, REGISTRY_WARDEN} from "../components/Permissions/Roles";
import {UploadButton} from "../components/Common/upload-button";
import {DnaResults} from "../components/Dna/dna-results";
import OffspringWidget from "../components/AnimalDashboard/Pedigree/offspring-widget";

const useRegistrations = (panonId: string) => useSuspenseQuery({
    queryKey: ['registrations', panonId],
    queryFn: async () => panonId ? RegistrationService.findRegistrationsByPanonId(panonId) : [],
});

const useRegistries = (registryIds: string[]) => useSuspenseQuery({
    queryKey: ['registries', registryIds],
    queryFn: async () => registryIds.length > 0 ? RegistryService.loadByIds(registryIds) : [],
});

const useRegistration = (registryId: string, panonId: string) => useSuspenseQuery({
    queryKey: ['registration', {registryId, panonId}],
    queryFn: async () => registryId && panonId ? RegistrationService.loadRegistration(registryId, panonId) : null,
});

const useWardenRegistryIds = () => useSuspenseQuery({
    queryKey: ['registries', 'of-warden'],
    queryFn: async () => RegistryService.findAllWhereBreederIsWarden(),
    select: registries => registries.map(r => r.id),
});

const useCreateCertificate = (registryId: string, panonId: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => await CertificateService.create(registryId, panonId),
        onSuccess: url => window.open(url),
        onSettled: async () => await queryClient.invalidateQueries({queryKey: ['certificates']}),
    });
};

const RegistrationsWidget = ({panonId, activeRegistryId, onClick}: {
    panonId: string,
    activeRegistryId: string,
    onClick: (registryId: string) => void
}) => {
    const {data: registrations} = useRegistrations(panonId);
    const {data: registries} = useRegistries(registrations.map(r => r.registryId));
    const activeRegistry = registries.find(r => r.id === activeRegistryId);
    return (
        <Widget displayName={"Registriert bei " + activeRegistry?.name}>
            {registries.length > 1
                ? <>
                    <Typography>Auch registriert bei:</Typography>
                    <List>
                        {registries.filter(r => r.id !== activeRegistryId).map(r => <ListItem key={r.id} disablePadding>
                            <ListItemButton onClick={() => onClick(r.id)}>
                                {r.name}
                            </ListItemButton>
                        </ListItem>)}
                    </List>
                </>
                : null}
        </Widget>
    );
};

const CreateCertificateButton = ({registration}: { registration: RegistrationJson }) => {
    const {mutate: createCertificate, isPending: createCertificatePending} = useCreateCertificate(registration.registryId, registration.panonIdentifier);

    return (
        <Button
            startIcon={<HistoryEdu/>}
            onClick={() => window.confirm('Tierpässe sind öffentlich einsehbar. Fortfahren?') && createCertificate()}
            disabled={createCertificatePending}>
            Neuen Tierpass erstellen
        </Button>
    );
}

const RegistrationCertificates = ({registration}: { registration: RegistrationJson }) => {
    const {data = [], isPending} = useQuery({
        queryKey: ['certificates', {registration: registration.id}],
        queryFn: async () => await CertificateService.list(registration),
    });

    if (!isPending && data.length === 0) {
        return (
            <InfoBox>Keine Tierpässe vorhanden</InfoBox>
        );
    }

    return (
        <List>
            {data.slice(0, 1).map(cert => (
                <ListItemButton key={cert.documentUrl} onClick={() => window.open(cert.documentUrl)}>
                    <ListItemIcon><FileDownload/></ListItemIcon>
                    <ListItemText secondary={cert.creationDate.toLocaleString()}>{cert.documentUrl.replace(/^.*\//, '')}</ListItemText>
                </ListItemButton>
            ))}
        </List>
    );
};

const CertificateWidget = ({animal, registration}: {
    animal: JsonAnimal,
    registration: RegistrationJson
}) => {
    const {data: breeder} = useBreederInfo();

    return (
        <Widget displayName={
            <Stack direction="row" alignItems="center">
                Tierpässe
                {animal.ownerId === breeder.id
                    ? <CreateCertificateButton registration={registration}/>
                    : null}
            </Stack>
        }>
            <RegistrationCertificates registration={registration}/>
        </Widget>
    );
};

const useDnaUpload = (animalId: string) => {
    const queryClient = useQueryClient();
    const {mutateAsync: upload, isPending: isUploading} = useMutation({
        mutationKey: ['dna-upload'],
        mutationFn: async (file: any) => await DnaService.upload(animalId, file),
        onSettled: async () => await queryClient.invalidateQueries({queryKey: ['dna', {animalId}]}),
        onError: () => alert('Fehler beim Hochladen des DNA-Ergebnisses!'),
    })
    return {upload, isUploading};
}

const DnaUploadButton = ({animalId}: { animalId: string }) => {
    const {upload, isUploading} = useDnaUpload(animalId);

    return (
        <UploadButton
            disabled={isUploading}
            startIcon={isUploading ? <CircularProgress size="1em"/> : <UploadFile/>}
            accept=".txt,.csv,text/plain,text/csv"
            onFileSelected={files => files && upload(files[0])}
        >
            {isUploading ? 'Laden ...' : 'DNA-Ergebnis hochladen'}
        </UploadButton>
    );
};

const DnaResultsWidget = ({animalId}: { animalId: string }) => {
    const isWarden = AuthService.hasRole([REGISTRY_WARDEN, PANON_ADMIN]);
    return (
        <Widget displayName={
            <Stack direction="row" alignItems="center">
                DNA-Ergebnisse
                {isWarden ? <DnaUploadButton animalId={animalId}/> : null}
            </Stack>
        }>
            <DnaResults animalId={animalId}/>
        </Widget>
    );
};

const RegisteredAnimalDetails = () => {
    const nav = useNav();
    const {error, success} = useGlobalSnackbarStore(state => state);
    const {panonId, registryId} = useParams<{ registryId: string, panonId: string }>();

    const {animal} = useSuspenseAnimal(panonId);
    const {data: registration} = useRegistration(registryId, panonId);
    const {data: wardenRegistryIds} = useWardenRegistryIds();

    if (!registration || !animal) {
        return <LinearProgress/>;
    }

    const isRegistryWarden = wardenRegistryIds.includes(registration.registryId);

    const handleBaseDataEdit = async (animalBaseData: AnimalBaseData) => {
        const editData: RegistrationsController$RegistrationEditJson = {
            breed: animalBaseData.breed,
            dateOfBirth: animalBaseData.dateOfBirth,
            countryOfBirth: animalBaseData.countryOfBirth,
            eyeColor: registration.phenotype.eyeColor,
            herdCode: animalBaseData.herdCode,
            name: animalBaseData.name,
            primaryColor: animalBaseData.fiberColor,
            secondaryColors: registration.phenotype.secondaryColors,
            sex: animalBaseData.sex
        };
        try {
            await RegistrationService.editRegistration(registration, editData);
            success("Daten erfolgreich aktualisiert!");
        } catch (e) {
            error("Aktualisierung fehlgeschlagen!");
        }
    }

    return (
        <FlexStack key={registration.id}>
            <BaseData
                onSubmit={handleBaseDataEdit}
                editable={isRegistryWarden}
                initialBaseData={transformFromRegistration(registration)}/>

            <CertificateWidget
                animal={animal}
                registration={registration}/>

            <DnaResultsWidget animalId={animal.id}/>

            <IdentificationsWidget animal={animal} disableAddNew editExistingAllowed={isRegistryWarden}/>

            <RegistrationsWidget
                activeRegistryId={registryId}
                onClick={id => nav.push(RouteService.REGISTERED_ANIMAL_DETAILS, {panonId, registryId: id})}
                panonId={panonId}/>

            <OwnerWidget ownerId={animal.ownerId} panonId={animal.panonIdentifier.id}/>

            <PedigreeWidget
                isRegistered
                animal={animal}
                isRegistryWarden={isRegistryWarden}
                expandedRedirectUrl={RouteService.expand(RouteService.REGISTERED_ANIMAL_DETAILS, {panonId, registryId})}/>

            <OffspringWidget animal={animal}/>
        </FlexStack>
    );
};

export default RegisteredAnimalDetails;
